import './styles/ImagePreviewer.scss';
import { System_DeviceOrientationMap } from '../assets/keyMap';
import { AppContext } from '../Context';
import { Fragment, useContext, useState, useRef, useEffect, ReactElement } from 'react';
import ZoomImg from './ZoomImg';
import { Filesystem, Directory, PermissionStatus } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { isPlatform } from '@ionic/react';
import ReactDOM from 'react-dom/client';
import { Mediastore } from '@agorapulse/capacitor-mediastore';
//components
import Spinner from './Spinner';

//controllers
import DatabaseInterfaceController from '../controllers/DatabaseInterfaceController';

//mui icons
import CloseIcon from '@mui/icons-material/Close';

const nonstate = {
    imageTouchInterval: 750
}

interface Props {
    src: string,
    style?: any,
    fillContainer?: boolean,
    downloadable?: boolean,
    shareable?: boolean,
    className?: string,
    onError?: any
}

const ImagePreviewer: React.FC<Props> = ({ src, style, fillContainer, downloadable, shareable, className, onError }) => {
    //contexts
    const appContext = useContext(AppContext);
    //states
    const [shouldShowImgPreview, SetShouldShowImgPreview] = useState<boolean>(false);
    const [currentAppListenerActions, SetCurrentAppListenerActions] = useState<any[] | null>(null);
    const [imgSrcLoaded, SetImgSrcLoaded] = useState<boolean>(false);
    const [loaderLblAnim, SetLoaderLblAnim] = useState('hide');
    //refs
    const imageTouchIntervalRef = useRef<any>(null);
    const downloadShareModeRef = useRef<string>(''); //download or share
    const imagePreviewRef = useRef<any>(null);
    const loaderLblAnimRef = useRef<string>(loaderLblAnim);
    //refs - interval
    const loaderLblAnimIntervalRef = useRef<any>(null);
    //controllers
    const databaseInterfaceController = new DatabaseInterfaceController(appContext);

    //watchers
    //test
    // useEffect(() => {
    //     console.log('ImagePreviewerTest: ' + imgSrcLoaded);
    // }, [imgSrcLoaded]);

    //initialize
    useEffect(() => {
        if (loaderLblAnimIntervalRef.current != null) {
            clearInterval(loaderLblAnimIntervalRef.current);
            loaderLblAnimIntervalRef.current = null;
        }

        loaderLblAnimIntervalRef.current = setInterval(() => {

            if (loaderLblAnimRef.current == 'hide')
                loaderLblAnimRef.current = 'show';
            else if (loaderLblAnimRef.current == 'show')
                loaderLblAnimRef.current = 'hide';

            SetLoaderLblAnim(loaderLblAnimRef.current);

        }, 1000);

        return (() => {
            if (imageTouchIntervalRef.current != null)
                clearInterval(imageTouchIntervalRef.current);
            if (loaderLblAnimIntervalRef.current != null)
                clearInterval(loaderLblAnimIntervalRef.current);
        });
    }, []);

    //watch for app listener update currentAppListenerActions
    useEffect(() => {
        if (!shouldShowImgPreview) {
            //only when imagePreview screen is off
            SetCurrentAppListenerActions([...appContext.appListenerActions]);
        }
    }, [appContext.appListenerActions]);

    //watch for database result actions
    useEffect(() => {
        if (shouldShowImgPreview && appContext.databaseActionModeQueue.length > 0 && appContext.databaseActionParamsQueue.length > 0 && appContext.databaseResultAction != null) {
            const ExecuteDatabaseResultAction = async () => {
                const [res, mode] = await appContext.databaseResultAction();

                switch (mode) {
                    case 'fetchImage':
                        if (!JSON.stringify(res).includes('_false')) {
                            switch (downloadShareModeRef.current) {
                                case 'download':
                                    DownloadImage(res);
                                    break;
                                case 'share':
                                    ShareImage(res);
                                    break;
                            }
                        }
                        break;
                }
            };

            switch (appContext.databaseActionModeQueue[0]) {
                case 'fetchImage':
                    ExecuteDatabaseResultAction();
                    break;
            }
        }
    }, [appContext.databaseActionModeQueue, appContext.databaseResultAction]);

    //watch for should show image preview, attach imagePreview to document, else remove imagePreview from document
    useEffect(() => {
        const appContainer = document.getElementsByClassName('App');
        if (shouldShowImgPreview) {
            imagePreviewRef.current = document.createElement('div');
            const root = ReactDOM.createRoot(imagePreviewRef.current);
            root.render(RenderImagePreview());

            if (appContainer.length > 0)
                appContainer[0].appendChild(imagePreviewRef.current)

            //override backbutton listener to close preview
            appContext.SetAppListenerActions([
                ['backButton', () => { SetShouldShowImgPreview(false); }],
                ['appStateChange', GetAppListenerAction('appStateChange')],
                ['appUrlOpen', GetAppListenerAction('appUrlOpen')]
            ]);
            //current version of app controller, need to add in all the listeners to counter 
        } else {
            if (imagePreviewRef.current != null && appContainer.length > 0)
                appContainer[0].removeChild(imagePreviewRef.current);

            //remove backbutton listener, revert back to original behavior
            //add backbutton listener to close preview
            if (currentAppListenerActions != null)
                appContext.SetAppListenerActions([...currentAppListenerActions]);
        }
    }, [shouldShowImgPreview]);

    //ui functions
    const OnImgTouchStart = () => {
        if (imageTouchIntervalRef.current != null)
            clearInterval(imageTouchIntervalRef.current);
        imageTouchIntervalRef.current = setInterval(() => {
            //used hold for nonstate.imageTouchInterval
            clearInterval(imageTouchIntervalRef.current);

            if (
                (downloadable == undefined || downloadable)
                && (shareable == undefined || shareable)
            ) {
                appContext.SetPopupMessage('Would you like to download or share\n the image ?');
                appContext.SetPopupButtons(['Download', 'Share', 'Cancel']);
                appContext.SetPopupActions([() => OnDownloadBtnClicked(), () => OnShareImgClicked(), () => { }]);
            } else if (
                downloadable == undefined || downloadable
            ) {
                appContext.SetPopupMessage('Would you like to download the image ?');
                appContext.SetPopupButtons(['Download', 'Cancel']);
                appContext.SetPopupActions([() => OnDownloadBtnClicked(), () => { }]);
            } else if (
                shareable == undefined || shareable
            ) {
                appContext.SetPopupMessage('Would you like to share the image ?');
                appContext.SetPopupButtons(['Share', 'Cancel']);
                appContext.SetPopupActions([() => OnShareImgClicked(), () => { }]);
            }
        }, nonstate.imageTouchInterval);
    }
    const OnImgTouchMove = () => {
        //user attempt to hold and zoom img
        if (imageTouchIntervalRef.current != null)
            clearInterval(imageTouchIntervalRef.current);
    }
    const OnImgTouchEnd = () => {
        if (imageTouchIntervalRef.current != null)
            clearInterval(imageTouchIntervalRef.current);
    }
    const OnDownloadBtnClicked = () => {
        appContext.SetWaitScreenMessage('Preparing image please wait');
        appContext.SetWaitScreenShouldShow(true);
        downloadShareModeRef.current = 'download';

        //fetch imageBase64String from server
        databaseInterfaceController.Queue('fetchImage', [
            ['fetchImage', 'true'],
            ['imageUrl', src]
        ]);
    }
    const OnShareImgClicked = async () => {
        appContext.SetWaitScreenMessage('Preparing image please wait');
        appContext.SetWaitScreenShouldShow(true);
        downloadShareModeRef.current = 'share';

        //fetch imageBase64String from server
        databaseInterfaceController.Queue('fetchImage', [
            ['fetchImage', 'true'],
            ['imageUrl', src]
        ]);
    }

    //render funtions
    const RenderImagePreview = () => {
        return (
            <div className={'ImagePreviewer-image-preview ' + (className? className : '') + ' ' + (appContext.deviceOrientation == System_DeviceOrientationMap.LANDSCAPE ? 'landscape' : 'portrait')}>
                <button className='ImagePreviewer-image-preview-background-mask' onClick={() => SetShouldShowImgPreview(false)}></button>
                <div className='ImagePreviewer-image-preview-img' onTouchStart={() => OnImgTouchStart()} onTouchEnd={() => OnImgTouchEnd()} onTouchMove={() => OnImgTouchMove()}><ZoomImg src={src} deviceOrientation={appContext.deviceOrientation} /></div>
                <button className='ImagePreviewer-image-preview-close-btn' onClick={() => SetShouldShowImgPreview(false)}><CloseIcon /></button>
            </div>
        )
    }

    //exe functions
    const SaveFileToLocal = async (directory: Directory, base64String: string, fileName: string) => {
        await Filesystem.writeFile({
            path: fileName,
            data: base64String, // your data to write (ex. base64)
            directory: /*Directory.Documents ---not working android 10 onwards*/Directory.Cache,
            recursive: true
        });
        const uri = await Filesystem.getUri({
            directory: directory,
            path: fileName
        });

        if (uri)
            return uri.uri;
        else
            return '';
    }
    const DownloadImage = async (base64String: string) => {
        const pathArr = src.split('/');
        const fileName = pathArr[pathArr.length - 1];

        if (isPlatform('hybrid')) {
            const uri = await SaveFileToLocal(Directory.Cache, base64String, fileName);
            const SaveFile = async () => {
                // const filePath = 'PPKM_Event_Gallery/' + fileName;
                // await Filesystem.writeFile({
                //     path: filePath,
                //     data: base64String, // your data to write (ex. base64)
                //     directory: Directory.Documents,
                //     recursive: true
                // });
                // appContext.SetPopupMessage('Downloaded Successfully!\nSaved in Documents/PPKM_Event_Gallery/');
                try {
                    await Mediastore.saveToDownloads({
                        path: uri,
                        filename: fileName
                    }).then((res: any) => {
                        console.log('saveToDownloadRes: ' + JSON.stringify(res));
                    }).catch((err: string) => {
                        console.log('saveToDownloadsErr: ' + err);
                    });
                    appContext.SetPopupMessage('Successfully saved to downloads folder.');
                } catch (err: any) {
                    console.log('getUriErr: ' + JSON.stringify(err));
                    appContext.SetPopupMessage('Fail to download picture.');
                }
            }
            await SaveFile();
        } else {
            var a = document.createElement("a"); //Create <a>
            a.href = "data:image/png;base64," + base64String; //Image Base64 Goes here
            a.download = fileName; //File name Here
            a.click(); //Downloaded file
            console.log('shouldDownloadImage');
        }
        appContext.SetWaitScreenShouldShow(false);
    }
    const ShareImage = async (base64String: string) => {
        if (isPlatform('hybrid')) {
            //save base64String to file *must save in local first before sharable
            const pathArr = src.split('/');
            const fileName = pathArr[pathArr.length - 1];
            try {
                appContext.SetWaitScreenMessage('Sharing Image Please Wait');
                appContext.SetWaitScreenShouldShow(true);
                const uri: string = await SaveFileToLocal(Directory.Cache, base64String, fileName);
                appContext.SetWaitScreenShouldShow(false);
                appContext.SetAppStateChangeWatch(false);
                Share.share({
                    text: appContext.user.name + ' is sharing an image via PPKM Super App',
                    files: [uri]
                }).then((res) => {
                    appContext.SetPopupMessage("Image successfully shared.");
                    appContext.SetAppStateChangeWatch(true);
                }).catch((err) => {
                    appContext.SetPopupMessage("Sharing failed!");
                    appContext.SetAppStateChangeWatch(true);
                });
            } catch (err) {
                console.log('some error while write file: ' + err);
            }
        } else {
            console.log('FileSharingNotSurportedInWeb');
        }
        appContext.SetWaitScreenShouldShow(false);
    }
    const GetAppListenerAction = (mode: string) => {
        let res = null;
        appContext.appListenerActions.map((actionArr: any[]) => {
            if (actionArr[0] == mode)
                res = actionArr[1];
        })

        return res;
    }

    return (
        <Fragment>
            <button className={'ImagePreviewer-btn ' + (fillContainer ? 'fill' : '') + (className ? className : '') + ' ' + (appContext.deviceOrientation == System_DeviceOrientationMap.LANDSCAPE ? 'landscape' : 'portrait')} style={style != undefined ? style : {}} onClick={() => SetShouldShowImgPreview(true)}>
                <img className={'ImagePreviewer-btn-img ' + (imgSrcLoaded ? 'show' : 'hide')} src={src} onLoad={() => SetImgSrcLoaded(true)} onError={onError ? onError : () => {}}/>
                {
                    !imgSrcLoaded ?
                        <div className='ImagePreviewer-img-loader'>
                            <Spinner size={75} interval={1500} colorTheme={['#e5514b', '#983531', '#b23e3a', '#ff6553']} />
                            <span className={'loader-lbl ' + (loaderLblAnim)}>loading</span>
                        </div>
                        :
                        null
                }
            </button>
        </Fragment>
    )
}
export default ImagePreviewer;